import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Camera, 
  Save, 
  Trash2, 
  User, 
  Mail, 
  Phone, 
  Building2,
  MapPin,
  Map,
  Briefcase,
  Users as UsersIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOGO_REGIONS = ["Maritime", "Plateaux", "Centrale", "Kara", "Savanes", "DAGL"];

const SECTORS = [
  "Agriculture",
  "Agroalimentaire",
  "Artisanat",
  "Commerce",
  "Construction/BTP",
  "Éducation/Formation",
  "Industrie",
  "Informatique/Tech",
  "Santé",
  "Services",
  "Tourisme/Hôtellerie",
  "Transport/Logistique",
  "Autre"
];

export const ProfilePage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company_name: user?.company_name || ''
  });
  
  const [companyData, setCompanyData] = useState({
    description: '',
    address: '',
    region: '',
    prefecture: '',
    commune: '',
    sector: '',
    employee_count: '',
    contact_person: '',
    contact_gender: '',
    contact_email: '',
    contact_phone: '',
    contact_whatsapp: '',
    geolocation: { latitude: '', longitude: '' }
  });
  
  const [loading, setLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [loadingCompanyData, setLoadingCompanyData] = useState(false);

  useEffect(() => {
    if (user?.role === 'sme') {
      fetchCompanyData();
    }
  }, [user]);

  const fetchCompanyData = async () => {
    setLoadingCompanyData(true);
    try {
      const response = await axios.get(`${API}/profile/company`);
      const data = response.data;
      setCompanyData({
        description: data.description || '',
        address: data.address || '',
        region: data.region || '',
        prefecture: data.prefecture || '',
        commune: data.commune || '',
        sector: data.sector || '',
        employee_count: data.employee_count?.toString() || '',
        contact_person: data.contact_person || '',
        contact_gender: data.contact_gender || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        contact_whatsapp: data.contact_whatsapp || '',
        geolocation: {
          latitude: data.geolocation?.latitude?.toString() || '',
          longitude: data.geolocation?.longitude?.toString() || ''
        }
      });
    } catch (error) {
      console.error('Erreur lors du chargement des données entreprise:', error);
    } finally {
      setLoadingCompanyData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/profile`, formData);
      toast.success('Profil mis à jour avec succès');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);

    try {
      const payload = {
        ...companyData,
        employee_count: companyData.employee_count ? parseInt(companyData.employee_count) : null,
        geolocation: (companyData.geolocation.latitude && companyData.geolocation.longitude)
          ? {
              latitude: parseFloat(companyData.geolocation.latitude),
              longitude: parseFloat(companyData.geolocation.longitude)
            }
          : null
      };
      
      await axios.put(`${API}/profile/company`, payload);
      toast.success('Informations entreprise mises à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo trop volumineuse (max 2Mo)');
      return;
    }

    setPhotoLoading(true);
    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      await axios.post(`${API}/profile/photo`, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo mise à jour');
      window.location.reload();
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      await axios.delete(`${API}/profile/photo`);
      toast.success('Photo supprimée');
      window.location.reload();
    } catch (error) {
      toast.error('Erreur lors de la suppression de la photo');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrateur',
      consultant: 'Consultant',
      sme: 'PME'
    };
    return labels[role] || role;
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="profile-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Mon profil</h1>
          <p className="text-slate-500 mt-1">Gérez vos informations personnelles</p>
        </div>

        {/* Photo de profil */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Photo de profil</CardTitle>
            <CardDescription>Votre photo sera visible par les autres utilisateurs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <Avatar className="h-24 w-24">
                {user?.profile_photo ? (
                  <AvatarImage src={user.profile_photo} alt={user.name} />
                ) : null}
                <AvatarFallback className="bg-slate-900 text-white text-2xl">
                  {user?.name?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                  data-testid="photo-upload-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoLoading}
                  data-testid="upload-photo-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {photoLoading ? 'Téléchargement...' : 'Changer la photo'}
                </Button>
                {user?.profile_photo && (
                  <Button
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleDeletePhoto}
                    data-testid="delete-photo-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Informations personnelles */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Informations personnelles</CardTitle>
            <CardDescription>Mettez à jour vos coordonnées</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Nom complet
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="profile-name-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="profile-email-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Téléphone
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+228 XX XX XX XX"
                  data-testid="profile-phone-input"
                  className="h-11"
                />
              </div>

              {user?.role === 'sme' && (
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-slate-700 font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Entreprise
                  </Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={formData.company_name}
                    disabled
                    className="h-11 bg-slate-50"
                  />
                  <p className="text-xs text-slate-500">Le nom de l'entreprise ne peut pas être modifié</p>
                </div>
              )}

              <Separator />

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Rôle</span>
                <span className="font-medium text-slate-900">{getRoleLabel(user?.role)}</span>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Enregistrement...
                  </span>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer les modifications
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Company Information - Only for SME users */}
        {user?.role === 'sme' && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Informations de l'entreprise
              </CardTitle>
              <CardDescription>Mettez à jour les informations de votre entreprise</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingCompanyData ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-6 w-6 border-4 border-slate-900 border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <form onSubmit={handleCompanySubmit} className="space-y-6">
                  {/* Description Section */}
                  <div className="space-y-2">
                    <Label>Description de l'entreprise</Label>
                    <textarea
                      placeholder="Décrivez brièvement votre entreprise, ses activités, ses services..."
                      value={companyData.description}
                      onChange={(e) => setCompanyData({ ...companyData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    />
                  </div>

                  <Separator />

                  {/* Location Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Localisation
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Région</Label>
                        <Select 
                          value={companyData.region || "none"} 
                          onValueChange={(v) => setCompanyData({...companyData, region: v === "none" ? "" : v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Non défini</SelectItem>
                            {TOGO_REGIONS.map(r => (
                              <SelectItem key={r} value={r}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Préfecture</Label>
                        <Input
                          placeholder="Préfecture"
                          value={companyData.prefecture}
                          onChange={(e) => setCompanyData({ ...companyData, prefecture: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Commune</Label>
                        <Input
                          placeholder="Commune"
                          value={companyData.commune}
                          onChange={(e) => setCompanyData({ ...companyData, commune: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Adresse</Label>
                        <Input
                          placeholder="Adresse complète"
                          value={companyData.address}
                          onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Geolocation Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Map className="w-4 h-4" />
                      Géolocalisation (optionnel)
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="ex: 6.1319"
                          value={companyData.geolocation.latitude}
                          onChange={(e) => setCompanyData({ 
                            ...companyData, 
                            geolocation: { ...companyData.geolocation, latitude: e.target.value }
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="ex: 1.2228"
                          value={companyData.geolocation.longitude}
                          onChange={(e) => setCompanyData({ 
                            ...companyData, 
                            geolocation: { ...companyData.geolocation, longitude: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Business Info Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Informations activité
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Secteur d'activité</Label>
                        <Select 
                          value={companyData.sector || "none"} 
                          onValueChange={(v) => setCompanyData({...companyData, sector: v === "none" ? "" : v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Non défini</SelectItem>
                            {SECTORS.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Nombre d'employés</Label>
                        <Input
                          type="number"
                          placeholder="ex: 15"
                          value={companyData.employee_count}
                          onChange={(e) => setCompanyData({ ...companyData, employee_count: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Contact Info Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <UsersIcon className="w-4 h-4" />
                      Contact entreprise
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2 col-span-2">
                          <Label>Personne de contact</Label>
                          <Input
                            placeholder="Nom du contact"
                            value={companyData.contact_person}
                            onChange={(e) => setCompanyData({ ...companyData, contact_person: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Genre</Label>
                          <Select 
                            value={companyData.contact_gender || "none"} 
                            onValueChange={(v) => setCompanyData({...companyData, contact_gender: v === "none" ? "" : v})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Genre" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Non défini</SelectItem>
                              <SelectItem value="M">Homme</SelectItem>
                              <SelectItem value="F">Femme</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Email de contact</Label>
                        <Input
                          type="email"
                          placeholder="contact@entreprise.tg"
                          value={companyData.contact_email}
                          onChange={(e) => setCompanyData({ ...companyData, contact_email: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Téléphone entreprise</Label>
                          <Input
                            placeholder="+228 XX XX XX XX"
                            value={companyData.contact_phone}
                            onChange={(e) => setCompanyData({ ...companyData, contact_phone: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>WhatsApp</Label>
                          <Input
                            placeholder="+228 XX XX XX XX"
                            value={companyData.contact_whatsapp}
                            onChange={(e) => setCompanyData({ ...companyData, contact_whatsapp: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={companyLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    data-testid="save-company-btn"
                  >
                    {companyLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                        Enregistrement...
                      </span>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Enregistrer les informations entreprise
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};
